import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './api/api';
import { AuthContext } from '../App';
import TrackPicker from './TrackPicker';
import UserImpersonationPicker from './UserImpersonationPicker';

const Testings = () => {
    const { setApiRequests } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const urlId = searchParams.get('id');

    const hasFetchedToday = useRef(false);
    const abortControllerRef = useRef(null);

    const [testingTrackId, setTestingTrackId] = useState(
        urlId ? parseInt(urlId, 10) : null
    );
    const [impersonateUserId, setImpersonateUserId] = useState('');
    const [submitError, setSubmitError] = useState(null);

    // Keep testingTrackId in sync with the URL — lets TrackPicker switch
    // tracks by just updating the `id` query param, without a full reload.
    useEffect(() => {
        if (urlId) setTestingTrackId(parseInt(urlId, 10));
    }, [urlId]);
    const [testingTemperature, setTestingTemperature] = useState('');
    const [trackName, setTrackName] = useState(null);

    const [driverInput, setDriverInput] = useState('');
    const [carInput, setCarInput] = useState('');

    const [setups, setSetups] = useState({});
    const [driverStats, setDriverStats] = useState({});
    const [carLevels, setCarLevels] = useState({});
    const [carWearArray, setCarWearArray] = useState([]);
    const [tyresLapsLife2, setTyresLapsLife2] = useState({});

    const [laps, setLaps] = useState(Array(10).fill(''));
    const [fuelData, setFuelData] = useState({});
    const [version, setVersion] = useState(1);
    const [ct, setCt] = useState(60);

    const [tyreSuppliers, setTyreSuppliers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState('');

    // Tyre suppliers
    useEffect(() => {
        const fetchTyreSuppliers = async () => {
            try {
                const res = await api.get('/tyre');
                const sorted = res.data.sort((a, b) => a.id - b.id);
                setTyreSuppliers(sorted);
            } catch (err) { console.error('Failed to fetch tyre suppliers:', err); }
        };
        fetchTyreSuppliers();
    }, []);

    // ── Fetch today ──────────────────────────────────────────────
    useEffect(() => {
        if (hasFetchedToday.current) return;
        hasFetchedToday.current = true;
        const fetchToday = async () => {
            try {
                const res = await api.get('/update/today');
                const data = res.data;
                if (data.apiRequestsRemaining !== undefined) setApiRequests(data.apiRequestsRemaining);
                if (data.testingTemperature !== undefined) setTestingTemperature(data.testingTemperature);
                if (!urlId && data.testingTrackId) setTestingTrackId(data.testingTrackId);
            } catch (err) { console.error('Failed to fetch today update:', err); }
        };
        fetchToday();
    }, [urlId]);

    // ── Fetch track name ─────────────────────────────────────────
    useEffect(() => {
        if (!testingTrackId) { setTrackName(null); return; }
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const fetchTrack = async () => {
            try {
                const res = await api.get(`/track/${testingTrackId}`, { signal: controller.signal });
                setTrackName(res.data.name);
            } catch (err) {
                if (err.name !== 'AbortError' && err.name !== 'CanceledError') console.error('Failed to fetch track name:', err);
                setTrackName(null);
            }
        };
        fetchTrack();
        return () => controller.abort();
    }, [testingTrackId]);

    // ── Parsers ──────────────────────────────────────────────────
    const carKeyMap = {
        chassis: 'chassis', engine: 'engine', 'front wing': 'frontWing',
        'rear wing': 'rearWing', underbody: 'underbody', sidepods: 'sidepods',
        cooling: 'cooling', gearbox: 'gearbox', brakes: 'brakes',
        suspension: 'suspension', electronics: 'electronics'
    };

    const parseDriver = (input) => {
        if (!input.trim()) return null;
        const driver = {};
        const lines = input.split('\n').map(l => l.trim()).filter(l => l);
        const energyLine = lines.find(l => /^\d+%/.test(l));
        if (energyLine) driver.energy = parseInt(energyLine, 10);
        lines.forEach(line => {
            const match = line.match(/^([A-Za-z\s\(\)]+):\s*(\d+)/);
            if (!match) return;
            const key = match[1].trim().toLowerCase().replace(/\s|\(|\)/g, '');
            const val = parseInt(match[2], 10);
            switch (key) {
                case 'overall': driver.overall = val; break;
                case 'concentration': driver.concentration = val; break;
                case 'talent': driver.talent = val; break;
                case 'aggressiveness': driver.aggressiveness = val; break;
                case 'experience': driver.experience = val; break;
                case 'technicalinsight': driver.technicalInsight = val; break;
                case 'stamina': driver.stamina = val; break;
                case 'charisma': driver.charisma = val; break;
                case 'motivation': driver.motivation = val; break;
                case 'reputation': driver.reputation = val; break;
                case 'weightkg': case 'weight': driver.weight = val; break;
                case 'age': driver.age = val; break;
                default: break;
            }
        });
        return driver;
    };

    const parseCar = (input) => {
        if (!input.trim()) return { car: null, carWear: null };
        const car = {}; const carWear = {};
        const lines = input.split('\n').map(l => l.trim()).filter(l => l);
        let currentKey = null;
        lines.forEach(line => {
            // Compact format (what /setup/testings echoes back): "Chassis: 2 48%".
            const labelMatch = line.match(/^([A-Za-z\s]+):\s*(\d+)(?:\s+(\d+)%)?/);
            if (labelMatch) {
                const key = carKeyMap[labelMatch[1].trim().toLowerCase()];
                if (!key) { currentKey = null; return; }
                car[key] = parseInt(labelMatch[2], 10);
                currentKey = key;
                if (labelMatch[3]) carWear[key] = parseInt(labelMatch[3], 10);
                return;
            }
            // GPRO's own car page paste spreads level and wear over several
            // lines per part (label line, "Don't replace", then a
            // "48%  2  48%" row) — pick up the wear% from whichever later
            // line has it, for the part whose label line we saw most recently.
            if (currentKey && carWear[currentKey] === undefined) {
                const wearMatch = line.match(/^(\d+)%/);
                if (wearMatch) carWear[currentKey] = parseInt(wearMatch[1], 10);
            }
        });
        return { car, carWear };
    };

    const formatDriverInput = (driver) => {
        if (!driver) return '';
        return [
            `Energy: ${driver.energy ?? ''}`,
            `Overall: ${driver.overall ?? ''}`,
            `Concentration: ${driver.concentration ?? ''}`,
            `Talent: ${driver.talent ?? ''}`,
            `Aggressiveness: ${driver.aggressiveness ?? ''}`,
            `Experience: ${driver.experience ?? ''}`,
            `Technical Insight: ${driver.technicalInsight ?? ''}`,
            `Stamina: ${driver.stamina ?? ''}`,
            `Charisma: ${driver.charisma ?? ''}`,
            `Motivation: ${driver.motivation ?? ''}`,
            `Reputation: ${driver.reputation ?? ''}`,
            `Weight(kg): ${driver.weight ?? ''}`,
            `Age: ${driver.age ?? ''}`,
        ].join('\n');
    };

    const formatCarInput = (car, wearStart) => {
        if (!car || !wearStart) return '';
        const parts = [
            { label: 'Chassis', key: 'chassis' },
            { label: 'Engine', key: 'engine' },
            { label: 'Front Wing', key: 'frontWing' },
            { label: 'Rear Wing', key: 'rearWing' },
            { label: 'Underbody', key: 'underbody' },
            { label: 'Sidepods', key: 'sidepods' },
            { label: 'Cooling', key: 'cooling' },
            { label: 'Gearbox', key: 'gearbox' },
            { label: 'Brakes', key: 'brakes' },
            { label: 'Suspension', key: 'suspension' },
            { label: 'Electronics', key: 'electronics' },
        ];
        return parts.map(p => `${p.label}: ${car[p.key] ?? ''} ${wearStart[p.key] ?? ''}%`).join('\n');
    };

    const handleLapChange = (index, value) => {
        const newLaps = [...laps];
        newLaps[index] = value;
        setLaps(newLaps);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        const driver = parseDriver(driverInput);
        const { car, carWear } = parseCar(carInput);
        const stints = {};
        laps.forEach((val, idx) => {
            if (val && val.trim() !== '') {
                const num = parseInt(val, 10);
                if (!isNaN(num) && num > 0) stints[idx + 1] = num;
            }
        });
        const payload = {
            ...(driver && Object.keys(driver).length > 0 && { driver }),
            ...(car && Object.keys(car).length > 0 && { car }),
            ...(carWear && Object.keys(carWear).length > 0 && { carWear }),
            ...(testingTrackId && { testingTrackId }),
            ...(testingTemperature !== '' && testingTemperature != null && { temperature: parseInt(testingTemperature, 10) }),
            stints,
            version,
            ...(version === 2 && { ct }),
            ...(impersonateUserId && { userId: parseInt(impersonateUserId, 10) }),
        };
        if (selectedSupplier && selectedSupplier.trim() !== '') payload.tyreSupplier = selectedSupplier;
        try {
            const res = await api.post('/setup/testings', payload);
            const data = res.data;
            if (data.apiRequestsRemaining !== undefined) setApiRequests(data.apiRequestsRemaining);
            setSetups(data.setups || {});
            setDriverStats(data.driver || {});
            setCarLevels(data.car || {});
            setCarWearArray(data.carWear || []);
            setTyresLapsLife2(data.tyresLapsLife2 || {});
            setFuelData(data.fuel || {});
            if (data.temperature != null) setTestingTemperature(data.temperature);
            if (data.driver) setDriverInput(formatDriverInput(data.driver));
            if (data.car && data.carWear && data.carWear.length > 0) setCarInput(formatCarInput(data.car, data.carWear[0]));
        } catch (err) {
            console.error('Failed to submit testings:', err);
            if (err.response?.status === 403) setSubmitError('Access denied.');
            else if (err.response?.status === 404) setSubmitError("Selected user has no linked GPRO token — can't calculate for them.");
            else setSubmitError('Failed to calculate testing setup.');
        }
    };

    const getCarWearValue = (index, partKey) => {
        if (!carWearArray[index]) return '';
        return carWearArray[index][partKey] ?? '';
    };

    const partKeysForStints = [
        { label: 'Chassis', key: 'chassis' },
        { label: 'Engine', key: 'engine' },
        { label: 'Front Wing', key: 'frontWing' },
        { label: 'Rear Wing', key: 'rearWing' },
        { label: 'Underbody', key: 'underbody' },
        { label: 'Sidepods', key: 'sidepods' },
        { label: 'Cooling', key: 'cooling' },
        { label: 'Gearbox', key: 'gearbox' },
        { label: 'Brakes', key: 'brakes' },
        { label: 'Suspension', key: 'suspension' },
        { label: 'Electronics', key: 'electronics' },
    ];

    // ── Render ───────────────────────────────────────────────────
    return (
        <>
            <div className="fb-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <div className="fb-page-title">
                        Testings
                        {trackName && <span style={{ fontWeight: 400, color: 'var(--c-text-muted)' }}> — {trackName}</span>}
                        {!trackName && testingTrackId && <span style={{ fontWeight: 400, color: 'var(--c-text-muted)' }}> — Track #{testingTrackId}</span>}
                    </div>
                    <div className="fb-page-sub">Some testing magic. A bit wrong, but magicious anyway.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <TrackPicker currentTrackId={testingTrackId} />
                    <UserImpersonationPicker value={impersonateUserId} onChange={setImpersonateUserId} />
                </div>
            </div>

            {submitError && (
                <div className="fb-alert fb-alert-error" style={{ marginBottom: 16, marginTop: 0 }}>{submitError}</div>
            )}

            <form onSubmit={handleSubmit}>
                {/* ── Input section ── */}
                <div className="fb-card">
                    <div className="fb-card-header"><span className="fb-card-title">Paste Data</span></div>
                    <div className="fb-card-body">
                        <div style={{ display: 'flex', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label className="fb-label">Driver Stats</label>
                                <textarea
                                    className="fb-textarea"
                                    rows={14}
                                    placeholder="Paste driver stats here…"
                                    value={driverInput}
                                    onChange={(e) => setDriverInput(e.target.value)}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label className="fb-label">Car Data</label>
                                <textarea
                                    className="fb-textarea"
                                    rows={14}
                                    placeholder="Paste car data here…"
                                    value={carInput}
                                    onChange={(e) => setCarInput(e.target.value)}
                                />
                            </div>
                        </div>
                        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--c-border)' }}>
                                {[1, 2].map(v => (
                                    <button key={v} type="button"
                                        onClick={() => setVersion(v)}
                                        style={{
                                            padding: '6px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                            background: version === v ? 'var(--c-accent)' : '#fff',
                                            color: version === v ? '#fff' : 'var(--c-text)',
                                            border: 'none', outline: 'none',
                                        }}>
                                        V{v}
                                    </button>
                                ))}
                            </div>
                            {version === 2 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <label className="fb-label" style={{ marginBottom: 0 }}>CT</label>
                                    <input type="number" className="fb-input fb-input-sm" style={{ width: 80 }}
                                        value={ct} onChange={(e) => setCt(parseInt(e.target.value, 10) || 0)} />
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <label className="fb-label" style={{ marginBottom: 0 }}>Tyre Supplier</label>
                                <select className="fb-select" style={{ width: 'auto' }}
                                    value={selectedSupplier} onChange={(e) => setSelectedSupplier(e.target.value)}>
                                    <option value="">Get from account</option>
                                    {tyreSuppliers.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                                </select>
                            </div>
                            <button type="submit" className="fb-btn fb-btn-primary">Calculate</button>
                        </div>
                    </div>
                </div>

                {/* ── Results row ── */}
                <div style={{ display: 'flex', gap: 16 }}>
                    {/* Setups */}
                    <div className="fb-card" style={{ flex: 1 }}>
                        <div className="fb-card-header"><span className="fb-card-title">Setups</span></div>
                        <div className="fb-table-wrap">
                            <table className="fb-table">
                                <tbody>
                                    {[
                                        { label: 'Temperature', value: testingTemperature },
                                        { label: 'Front Wing', value: setups.frontWing },
                                        { label: 'Rear Wing', value: setups.rearWing },
                                        { label: 'Engine', value: setups.engine },
                                        { label: 'Brakes', value: setups.brakes },
                                        { label: 'Gearbox', value: setups.gearbox },
                                        { label: 'Suspension', value: setups.suspension },
                                    ].map(({ label, value }) => (
                                        <tr key={label}>
                                            <td style={{ color: 'var(--c-text-muted)', fontWeight: 500 }}>{label}</td>
                                            <td style={{ fontWeight: 600 }}>{value ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Tyre life */}
                    <div className="fb-card" style={{ flex: 1 }}>
                        <div className="fb-card-header"><span className="fb-card-title">Tyre Life</span></div>
                        <div className="fb-table-wrap">
                            <table className="fb-table">
                                <thead>
                                    <tr>
                                        <th>Compound</th>
                                        <th>Laps</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {['Extra Soft', 'Soft', 'Medium', 'Hard', 'Rain'].map(c => (
                                        <tr key={c}>
                                            <td>{c}</td>
                                            <td style={{ fontWeight: 600 }}>{tyresLapsLife2[c] ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ── Stints ── */}
                <div className="fb-card">
                    <div className="fb-card-header"><span className="fb-card-title">Stints</span></div>
                    <div className="fb-table-wrap">
                        <table className="fb-table fb-table-sm">
                            <thead>
                                <tr>
                                    <th>Part</th>
                                    <th>Current</th>
                                    {laps.map((_, i) => <th key={i}>Stint {i + 1}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ fontWeight: 600 }}>Laps</td>
                                    <td></td>
                                    {laps.map((value, i) => (
                                        <td key={i}>
                                            <input
                                                type="number" min="1" max="50"
                                                value={value}
                                                onChange={(e) => handleLapChange(i, e.target.value)}
                                            />
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 600 }}>Fuel</td>
                                    <td></td>
                                    {laps.map((_, i) => <td key={i}>{fuelData[String(i + 1)] ?? ''}</td>)}
                                </tr>
                                {partKeysForStints.map((part) => (
                                    <tr key={part.key}>
                                        <td>{part.label}</td>
                                        <td style={{ color: 'var(--c-text-muted)' }}>{getCarWearValue(0, part.key)}</td>
                                        {laps.map((_, i) => (
                                            <td key={i}>{getCarWearValue(i + 1, part.key)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                    <button type="submit" className="fb-btn fb-btn-primary">Calculate</button>
                </div>
            </form>
        </>
    );
};

export default Testings;
